// ============================================================
// liveLookStore — preferencia visual "Live looks like Dev".
//
// Cuando es true, Live Monitor usa el mismo color que Developer
// (cyan) en headers y aggregate bar. NO cambia el badge "🟢 Live"
// del session card — eso refleja status real del bot, no preferencia.
//
// Default por usuario:
//   - ismael@rabbyt.ai → forzado true (no se puede desactivar)
//   - developer@walker-blue.com → true inicial (puede toggle)
//   - resto → false (puede toggle)
//
// Persistencia: localStorage. Para usuarios "forzados" siempre devuelve true,
// ignorando lo que haya en storage.
// ============================================================

const STORAGE_KEY = 'rhdash:liveLookLikeDev';

// Usuarios con override forzado (siempre true, toggle deshabilitado).
const FORCED_USERS = new Set<string>(['ismael@rabbyt.ai', 'nelson@rabbyt.ai']);

// Usuarios con default true al primer boot (si no hay nada en storage).
const DEFAULT_TRUE_USERS = new Set<string>(['ismael@rabbyt.ai', 'nelson@rabbyt.ai']);

interface LiveLookState {
  liveLooksLikeDev: boolean;
}

function readStored(email: string | null): LiveLookState {
  if (typeof window === 'undefined') return { liveLooksLikeDev: false };

  // Forced users: siempre true, ignora storage.
  if (email && FORCED_USERS.has(email)) {
    return { liveLooksLikeDev: true };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw !== null) {
      const parsed = JSON.parse(raw) as Partial<{ liveLooksLikeDev: boolean }>;
      return { liveLooksLikeDev: parsed.liveLooksLikeDev === true };
    }
  } catch {
    // ignore
  }

  // Primer boot sin storage: aplicar default por email.
  if (email && DEFAULT_TRUE_USERS.has(email)) {
    return { liveLooksLikeDev: true };
  }

  return { liveLooksLikeDev: false };
}

export const liveLookStore = $state<LiveLookState>({ liveLooksLikeDev: false });

/** Llamar tras loadAuth (cuando ya tenemos el email del user). */
export function initLiveLook(email: string | null): void {
  const initial = readStored(email);
  liveLookStore.liveLooksLikeDev = initial.liveLooksLikeDev;
}

/** True si el usuario actual NO puede toggle (forzado). */
export function isLiveLookForced(email: string | null): boolean {
  return email !== null && FORCED_USERS.has(email);
}

export function setLiveLooksLikeDev(value: boolean, email: string | null): void {
  // No-op si el user está forzado.
  if (isLiveLookForced(email)) return;
  liveLookStore.liveLooksLikeDev = value;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ liveLooksLikeDev: value }));
  } catch {
    // ignore
  }
}
